import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Icon } from '@brighterion/br-ui';
import Table from 'Components/Table';
import _ from 'lodash';
import { getReadableBytes } from 'Utils';
import styles from './index.css';

type Props = {
    previewModalStatus: boolean;
    parsedFileTableData?: ParsedFileTableData;
    setPreviewModalStatus: (status: boolean) => void;
    submit: () => void;
    uploadedFile?: File;
    rulesTypeText: string;
    previewTitle?: string;
    previewContentRenderer: (parsedFileTableData: ParsedFileTableData) => JSX.Element;
};

export default function PreviewModal({
    parsedFileTableData,
    previewModalStatus,
    setPreviewModalStatus,
    submit,
    uploadedFile,
    rulesTypeText,
    previewTitle,
    previewContentRenderer
}: Props) {
    const { t } = useTranslation();
    const columns = useMemo(() => (parsedFileTableData ? parsedFileTableData.columns : []), [parsedFileTableData]);
    const dataToShow = useMemo(() => {
        if (parsedFileTableData) {
            const dataLength = parsedFileTableData?.data?.length;
            const maxRowIndex = dataLength < 50 ? dataLength : 50;

            return parsedFileTableData?.data?.slice(0, maxRowIndex);
        }
        return [];
    }, [parsedFileTableData]);

    const { fileName, fileSize, iconName } = useMemo(() => {
        if (uploadedFile)
            return {
                fileSize: getReadableBytes(uploadedFile.size),
                fileName: uploadedFile.name,
                iconName: `${uploadedFile.name.split('.')[1]}-file`
            };
        return {};
    }, [uploadedFile]);
    if (parsedFileTableData && uploadedFile && !_.isEmpty(parsedFileTableData)) {
        return (
            <Modal
                isOpen={previewModalStatus}
                closeModal={() => setPreviewModalStatus(false)}
                closeOnClickOutside={false}
            >
                <Modal.Title
                    id='preview-modal'
                    title={previewTitle || t('preview dialog title', { rulesTypeText })}
                    onClose={() => setPreviewModalStatus(false)}
                >
                    <div data-testid='uploadedFileDetails' className={styles.uploadedFileDetails}>
                        <Icon className={styles.uploadedFileIcon} size='xlg' name={iconName || ''} />
                        <div>{fileName}</div>
                        <div className={styles.fileSize}>{fileSize}</div>
                    </div>
                </Modal.Title>
                <div data-testid='previewInstructions' className={styles.previewInstructions}>
                    {previewContentRenderer(parsedFileTableData)}
                    {t('A preview of the first (up to) 50 rows is shown below Please review before uploading.')}
                </div>
                <div className={styles.tableOverflowContainer}>
                    <Table
                        tableName='upload-preview-table'
                        dataTestId='upload-preview-table'
                        data={dataToShow}
                        columns={columns}
                    />
                </div>
                <Modal.Footer
                    onActionText={t('Upload')}
                    onClose={() => setPreviewModalStatus(false)}
                    onAction={() => submit()}
                />
            </Modal>
        );
    }
    return null;
}
